/**
 * meetingForm — the calendar editor's form model (#680), lifted from the
 * deleted ScheduleMeetingModal so the page component carries layout only.
 *
 * The CREATE payload is a contract (#514/#540 tests): for the same inputs it is
 * byte-identical to what the modal sent — naive `${date}T${time}:00` start/end
 * AUTHORED IN the selected `timezone` ("reinterpret, don't renumber"; the
 * backend resolves them in that zone), `location` as the display label, both
 * conference flags from the (calendar, location) pair, `contact_ids`. The only
 * additions are `all_day: true` and the repeat / show-as / optional-attendee
 * keys, each sent only when set. Tags (`form.tags`, #680) are NEVER in either
 * payload: they are saved by a second call (useSaveEventTags) after the event.
 */
import { buildLocationOptions, conferenceFlags, locationLabel } from '../../components/schedule/locationOptions';
import { formatInZone, naiveInZone } from '../../lib/timezone';
import { eventDayKey } from '../../lib/calendar/zonedCalendar';

export const REQUIRED_COPY = 'Title, date and times are required';

/** ScheduleMeetingModal's default rule: keep a valid prior choice, else Google, else the first. */
export function pickDefaultCalendar(calendars, prev) {
    if (!calendars || calendars.length === 0) return prev ?? null;
    if (prev && calendars.some((c) => c.value === prev)) return prev;
    if (calendars.some((c) => c.value === 'google')) return 'google';
    return calendars[0].value;
}

/**
 * The "Add online meeting" toggle IS buildLocationOptions (#680 D14): on means
 * the hosting calendar's conference option, off means 'none'. Returns null when
 * the calendar can mint no conference (no calendar selected yet).
 */
export function conferenceOptionFor(calendar) {
    return buildLocationOptions(calendar).find((o) => o.value !== 'none') || null;
}

export function locationFor(calendar, online) {
    const conf = conferenceOptionFor(calendar);
    return online && conf ? conf.value : 'none';
}

/** An instant → the wall-clock fields the editor edits, in `zone`. */
export function wallClockOf(instant, zone) {
    return {
        date: formatInZone(instant, { zone, formatStr: 'yyyy-MM-dd' }),
        time: formatInZone(instant, { zone, formatStr: 'HH:mm' }),
    };
}

const toMinutes = (hhmm) => {
    const [h, m] = String(hhmm || '').split(':').map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : NaN;
};
const fromMinutes = (min) => {
    const clamped = Math.max(0, Math.min(min, 23 * 60 + 59));
    return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
};

/** Move the start and keep the duration (the editor and its day timeline both do this). */
export function shiftStart(form, newStart) {
    const duration = toMinutes(form.endTime) - toMinutes(form.startTime);
    const end = toMinutes(newStart) + (Number.isFinite(duration) && duration > 0 ? duration : 30);
    return { ...form, startTime: newStart, endTime: fromMinutes(end) };
}

export function durationMinutes(form) {
    return toMinutes(form.endTime) - toMinutes(form.startTime);
}

/** @returns {string|null} the first validation error, or null */
export function validateMeetingForm(form) {
    if (!form.title || !form.date || !form.startTime || !form.endTime) return REQUIRED_COPY;
    if (!form.allDay && durationMinutes(form) <= 0) return 'The end time must be after the start time';
    return null;
}

/** Attendee chips → the two payload lists (#680 D15). */
export function attendeeLists(attendees) {
    const emails = [];
    const contactIds = [];
    for (const a of attendees || []) {
        if (a?.email && !emails.includes(a.email)) emails.push(a.email);
        if (a?.contact_id && !contactIds.includes(a.contact_id)) contactIds.push(a.contact_id);
    }
    return { emails, contactIds };
}

/*
 * Repeat ("cadence") — the prototype's four choices, each anchored on the
 * event's own date. Only the KEY is sent; the backend derives the weekday and
 * week-of-month from the same wall date (services/calendar_recurrence.py), so
 * these labels and the booked rule cannot disagree. A fifth-week date books
 * "last", on both sides, so a series never skips a month.
 */
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ORDINALS = { 1: 'first', 2: 'second', 3: 'third', 4: 'fourth', [-1]: 'last' };

export function weekOfMonth(dayOfMonth) {
    const n = Math.floor((dayOfMonth - 1) / 7) + 1;
    return n === 5 ? -1 : n;
}

export function repeatOptions(dateKey) {
    const options = [
        { value: 'none', label: 'Does not repeat' },
        { value: 'weekdays', label: 'Every weekday' },
    ];
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey || '');
    if (!m) return options;
    const day = Number(m[3]);
    const weekday = WEEKDAY_NAMES[new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, day)).getUTCDay()];
    return [
        ...options,
        { value: 'weekly', label: `Weekly on ${weekday}` },
        { value: 'monthly_nth', label: `Monthly on the ${ORDINALS[weekOfMonth(day)]} ${weekday}` },
    ];
}

/*
 * Show as — the prototype's Busy button cycles Busy → Free → Tentative.
 * Google has no Tentative for the organizer's own event (only Busy/Free), so
 * the cycle skips it there; the backend refuses it too.
 */
export const SHOW_AS_LABELS = { busy: 'Busy', free: 'Free', tentative: 'Tentative' };

export function showAsChoices(calendar) {
    return calendar === 'outlook' ? ['busy', 'free', 'tentative'] : ['busy', 'free'];
}

export function nextShowAs(current, calendar) {
    const choices = showAsChoices(calendar);
    const i = choices.indexOf(current);
    return choices[(i + 1) % choices.length];
}

/** Keep the show-as valid when the hosting calendar changes. */
export function showAsFor(calendar, current) {
    return showAsChoices(calendar).includes(current) ? current : 'busy';
}

export function buildCreatePayload(form) {
    const { emails, contactIds } = attendeeLists(form.attendees);
    const optional = attendeeLists(form.optionalAttendees);
    const location = locationFor(form.calendar, form.online);
    const startTime = form.allDay ? '00:00' : form.startTime;
    const endTime = form.allDay ? '00:00' : form.endTime;
    const payload = {
        title: form.title,
        description: form.description,
        start: `${form.date}T${startTime}:00`,
        end: `${form.date}T${endTime}:00`,
        location: locationLabel(location),
        attendees: emails,
        ...conferenceFlags(form.calendar, location),
        calendar: form.calendar,
        timezone: form.timezone,
        contact_ids: contactIds,
    };
    if (form.allDay) payload.all_day = true;
    // The three #680 additions are sent only when set, so an event without
    // them sends exactly what it always did. Busy is both providers' default.
    if (form.repeat && form.repeat !== 'none') payload.repeat = form.repeat;
    if (form.showAs && form.showAs !== 'busy') payload.show_as = form.showAs;
    const optionalEmails = optional.emails.filter((e) => !emails.includes(e));
    if (optionalEmails.length) {
        payload.optional_attendees = optionalEmails;
        payload.contact_ids = [...contactIds, ...optional.contactIds.filter((id) => !contactIds.includes(id))];
    }
    return payload;
}

/**
 * The UPDATE payload for useUpdateCalendarEvent. Start/end go as UTC instants
 * (what the old inline editor sent) resolved from the wall clock in the chosen
 * zone, plus that zone. Conference flags are never sent on update — minting is
 * create-only (Graph would issue a second Teams meeting; see _to_graph_event).
 * Attendees travel only for sources whose update route accepts them.
 */
export function buildUpdatePayload(event, form, initial = null) {
    const payload = { eventId: event.event_id, source: event.source, title: form.title };
    // An existing all-day event keeps its dates: the update routes take
    // instants, and re-sending a zone-midnight instant shifts the date on both
    // providers. Its title, notes and attendees still save.
    if (!event.all_day) {
        payload.start = naiveInZone(`${form.date}T${form.startTime}`, form.timezone);
        payload.end = naiveInZone(`${form.date}T${form.endTime}`, form.timezone);
        payload.timezone = form.timezone;
    }
    if (!initial || form.description !== initial.description) payload.description = form.description;
    if (event.source === 'google' || event.source === 'crm') {
        payload.attendees = attendeeLists(form.attendees).emails;
    }
    return payload;
}

const HTML_RE = /<\/?[a-z][\s\S]*>/i;

/**
 * Provider descriptions are often HTML (Google). The editor's notes field is
 * plain text, so an HTML description is shown as its text — and, because that
 * conversion loses formatting, buildUpdatePayload sends the description only
 * when the user actually changed it.
 */
export function descriptionAsText(description) {
    const raw = description || '';
    if (!HTML_RE.test(raw)) return raw;
    const withBreaks = raw.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|li|h[1-6])>/gi, '\n');
    const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
    return (doc.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
}

/** Sources whose update route takes attendees (routes/calendar.py google/crm branches). */
export function canEditAttendees(event) {
    return !event || event.source === 'google' || event.source === 'crm';
}

/** The editor's initial state for a new event or an existing one. */
export function initialForm({ event, start, end, zone, calendar }) {
    if (event) {
        const s = wallClockOf(event.start, zone);
        const e = wallClockOf(event.end, zone);
        return {
            title: event.title || '',
            description: descriptionAsText(event.description),
            // An all-day event's date is its calendar date, not its start's
            // zone wall date (a day early west of UTC).
            date: event.all_day ? eventDayKey(event, zone) || s.date : s.date,
            startTime: s.time,
            endTime: e.date === s.date ? e.time : '23:59',
            allDay: Boolean(event.all_day),
            timezone: zone,
            calendar: event.source === 'outlook' ? 'outlook' : event.source === 'google' ? 'google' : calendar,
            online: Boolean(event.conference_link),
            attendees: (event.attendees || []).filter((a) => a?.email).map((a) => ({ email: a.email, name: a.name || '' })),
            // Create-only controls; an existing event's series, show-as and
            // optional list are changed in the provider (no update route takes them).
            repeat: 'none',
            showAs: 'busy',
            optionalAttendees: [],
            // Tag NAMES, as TagInput holds them; the ids stay on event.tags.
            tags: (event.tags || []).filter((t) => t?.name).map((t) => t.name),
        };
    }
    const s = start ? wallClockOf(start, zone) : { date: '', time: '09:00' };
    const e = end ? wallClockOf(end, zone) : { time: '09:30' };
    return {
        title: '',
        description: '',
        date: s.date,
        startTime: s.time,
        endTime: e.time,
        allDay: false,
        timezone: zone,
        calendar,
        online: true,
        attendees: [],
        repeat: 'none',
        showAs: 'busy',
        optionalAttendees: [],
        tags: [],
    };
}

/**
 * Did anything the PROVIDER stores change? Everything but `tags`, which are
 * CRM-side (#682). An edit that changes only tags — or nothing — must not PUT
 * the event: the update route re-sends title, times and attendees to Google /
 * Outlook, which rewrites the event and can re-notify its guests.
 */
export function providerFieldsChanged(form, initial) {
    if (!initial) return true;
    const { tags: _a, ...now } = form;
    const { tags: _b, ...was } = initial;
    return JSON.stringify(now) !== JSON.stringify(was);
}
